import type { JSXProps, JSXChild } from 'engine/index';
import { Button } from './Button';
import { Spacer } from './Spacer';

type ModalProps = {
	title?: string;
	description?: string;
	content?: HTMLElement;
	cancel?: string | HTMLElement | null;
	confirm?: string | HTMLElement | null;
	dangerous?: boolean;
} & JSXProps;
export function Modal({ title, description, content, cancel, confirm, dangerous }: ModalProps, children: JSXChild[]) {
	const cancelEl =
		cancel === null ? null : cancel instanceof HTMLElement ? (
			cancel
		) : (
			<Button action-cancel mode={dangerous ? 'primary' : 'secondary'}>
				{cancel ?? 'Cancel'}
			</Button>
		);
	const confirmEl =
		confirm === null ? null : confirm instanceof HTMLElement ? (
			confirm
		) : (
			<Button action-confirm mode={dangerous ? 'secondary' : 'primary'}>
				{confirm ?? 'Confirm'}
			</Button>
		);

	return (
		<div class="rounded-lg border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-800">
			{title && <h2 class="mb-2 text-xl font-bold text-gray-800 dark:text-gray-200">{title}</h2>}
			{description && <p class="mb-10 max-w-[40ch] text-gray-700 dark:text-gray-300">{description}</p>}
			{content}
			{children}
			{(cancelEl || confirmEl) && (
				<div class="flex gap-3">
					{cancelEl}
					<Spacer />
					{confirmEl}
				</div>
			)}
		</div>
	);
}

export function showModal(
	modal: HTMLElement | ((confirm: () => void, cancel: () => void) => HTMLElement),
	settings: { passiveDismiss: boolean } = { passiveDismiss: false },
): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		const confirm = () => {
			resolve(true);
			remove();
			return;
		};

		const cancel = () => {
			resolve(false);
			remove();
			return;
		};

		const modal2: HTMLElement = typeof modal === 'function' ? modal(confirm, cancel) : modal;

		const view: HTMLElement = (
			<div
				on:click={(e: MouseEvent) => {
					if (settings.passiveDismiss && e.target === view) {
						remove();
					}
				}}
				class="fixed inset-0 z-[99] flex max-h-screen items-center justify-center overflow-hidden bg-black/30 p-8"
			>
				{modal2}
			</div>
		);
		let removing = false;

		const handleClick = (e: MouseEvent) => {
			if (removing) return;

			if (e.target && e.target instanceof HTMLElement) {
				if (e.target.hasAttribute('action-cancel') || e.target.closest('[action-cancel]')) {
					cancel();
					return;
				}

				if (e.target.hasAttribute('action-confirm') || e.target.closest('[action-confirm]')) {
					confirm();
					return;
				}
			}
		};

		const remove = () => {
			removing = true;
			view.animate([{ backgroundColor: view.style.backgroundColor }, { backgroundColor: 'transparent' }], {
				duration: 200,
				easing: 'ease-out',
				fill: 'forwards',
			});

			modal2
				.animate(
					[
						{ transform: 'translateY(0rem)', opacity: '1' },
						{ transform: 'translateY(8rem)', opacity: '0' },
					],
					{
						duration: 300,
						easing: 'cubic-bezier(0, 0.56, 0, 0.99)',
						fill: 'forwards',
					},
				)
				.finished.then(() => view.remove());
		};

		view.addEventListener('click', handleClick);

		if (!modal2.style.willChange) {
			modal2.style.willChange = 'transform';
		}

		const app = document.querySelector('#app') as HTMLElement;
		app.append(view);

		view.animate([{ backgroundColor: 'transparent' }, { backgroundColor: view.style.backgroundColor }], {
			duration: 300,
			easing: 'ease-out',
		});

		modal2.animate(
			[
				{ transform: 'translateY(8rem)', opacity: '0.4' },
				{ transform: 'translateY(0rem)', opacity: '1' },
			],
			{
				duration: 400,
				easing: 'cubic-bezier(0, 0.56, 0, 0.99)',
			},
		);
	});
}
